import { useMemo, useState } from "react";
import { GripVertical, AlertTriangle, Lock, Calendar as CalendarIcon, User as UserIcon, CheckCircle2 } from "lucide-react";
import { initials, colorFor, displayName } from "@/lib/avatar";
import { formatDuration } from "@/lib/duration";

// Phase 1.5 — Kanban board grouped by phase (Pre / Production / Post).
// Default per user choice: dragging a card across columns reassigns the
// `phase` field only — schedule is untouched (Production tasks anchored to
// call sheets cannot be re-phased because they're physical reality).

const COLUMNS = [
  {
    key: "pre",
    label: "Pre-Production",
    accent: "border-teal-500/60",
    chip: "bg-teal-500/10 text-teal-300 border-teal-500/40",
    dot: "bg-teal-500",
  },
  {
    key: "prod",
    label: "Production",
    accent: "border-amber-500/60",
    chip: "bg-amber-500/10 text-amber-300 border-amber-500/40",
    dot: "bg-amber-500",
  },
  {
    key: "post",
    label: "Post-Production",
    accent: "border-violet-500/60",
    chip: "bg-violet-500/10 text-violet-300 border-violet-500/40",
    dot: "bg-violet-500",
  },
];

export default function KanbanBoard({
  tasks = [],
  members = [],
  conflictingTaskIds = new Set(),
  onTaskClick,
  onChangePhase,
  readonly = false,
}) {
  const membersById = useMemo(() => {
    const m = {};
    members.forEach((u) => { m[u.id || u.user_id] = u; });
    return m;
  }, [members]);

  // Group tasks by phase, preserving start-date order inside each column.
  const grouped = useMemo(() => {
    const g = { pre: [], prod: [], post: [] };
    tasks.forEach((t) => {
      if (g[t.phase]) g[t.phase].push(t);
    });
    Object.values(g).forEach((arr) =>
      arr.sort((a, b) => (a.start || "").localeCompare(b.start || "")),
    );
    return g;
  }, [tasks]);

  const [dragOverCol, setDragOverCol] = useState(null);

  const handleDragStart = (e, task) => {
    if (readonly) {
      e.preventDefault();
      return;
    }
    // Production-synced tasks (linked to a call sheet) are anchored — can't re-phase
    if (task.phase === "prod" && task.call_sheet_id) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData("text/plain", task.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, colKey) => {
    if (readonly) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverCol !== colKey) setDragOverCol(colKey);
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOverCol(null);
  };

  const handleDrop = (e, colKey) => {
    e.preventDefault();
    setDragOverCol(null);
    if (readonly) return;
    const taskId = e.dataTransfer.getData("text/plain");
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.phase === colKey) return;
    if (task.phase === "prod" && task.call_sheet_id) return;
    onChangePhase?.(task, colKey);
  };

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-4"
      data-testid="kanban-board"
    >
      {COLUMNS.map((col) => {
        const colTasks = grouped[col.key] || [];
        const isDraggingOver = dragOverCol === col.key;
        return (
          <div
            key={col.key}
            data-testid={`kanban-column-${col.key}`}
            onDragOver={(e) => handleDragOver(e, col.key)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, col.key)}
            className={`rounded-md border bg-zinc-950/60 ${
              isDraggingOver
                ? `${col.accent} bg-zinc-900/70`
                : "border-zinc-800"
            }`}
          >
            <div className="px-3 py-2.5 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${col.dot}`} />
                <span className="text-overline text-zinc-300">{col.label}</span>
              </div>
              <span className="font-mono text-xs text-zinc-500" data-testid={`kanban-count-${col.key}`}>
                {colTasks.length}
              </span>
            </div>
            <ul className="p-2 space-y-2 min-h-[160px]">
              {colTasks.length === 0 && (
                <li className="text-center text-xs text-zinc-600 py-8 select-none">
                  No tasks
                </li>
              )}
              {colTasks.map((task) => (
                <KanbanCard
                  key={task.id}
                  task={task}
                  member={membersById[task.assigned_user_id]}
                  conflict={conflictingTaskIds.has(task.id)}
                  draggable={
                    !readonly && !(task.phase === "prod" && task.call_sheet_id)
                  }
                  onDragStart={(e) => handleDragStart(e, task)}
                  onClick={() => onTaskClick?.(task)}
                />
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function KanbanCard({ task, member, conflict, draggable, onDragStart, onClick }) {
  const lockedProd = task.phase === "prod" && task.call_sheet_id;
  const isDone = task.kanban_status === "done";
  return (
    <li
      data-testid={`kanban-card-${task.id}`}
      data-done={isDone ? "true" : undefined}
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onClick}
      className={`group relative rounded-sm border bg-zinc-900/60 p-2.5 transition-colors cursor-pointer hover:border-zinc-700 ${
        conflict
          ? "border-rose-700/60 bg-rose-950/30"
          : isDone
          ? "border-emerald-800/60 bg-emerald-950/20"
          : lockedProd
          ? "border-amber-900/60"
          : "border-zinc-800"
      } ${isDone ? "opacity-60" : ""} ${draggable ? "" : "cursor-not-allowed"}`}
      style={{ touchAction: "manipulation" }}
    >
      {isDone && (
        <span
          data-testid={`kanban-card-done-badge-${task.id}`}
          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm"
          title="Marked complete"
        >
          <CheckCircle2 className="w-3 h-3 text-emerald-950" strokeWidth={3} />
        </span>
      )}
      <div className="flex items-start gap-2">
        {draggable && !isDone && (
          <GripVertical className="w-3 h-3 text-zinc-600 mt-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
        {lockedProd && (
          <Lock className="w-3 h-3 text-amber-500/80 mt-0.5 shrink-0" title="Production-synced" />
        )}
        <div className="flex-1 min-w-0">
          <div
            className={`text-sm leading-tight font-medium truncate ${
              isDone ? "text-zinc-400 line-through" : "text-zinc-100"
            }`}
          >
            {task.name}
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-[10px] font-mono text-zinc-500">
            <span className="inline-flex items-center gap-1">
              <CalendarIcon className="w-2.5 h-2.5" />
              {task.start}
            </span>
            <span>· {formatDuration(task.duration)}</span>
            {conflict && (
              <span className="inline-flex items-center gap-1 text-rose-400">
                <AlertTriangle className="w-2.5 h-2.5" />
                conflict
              </span>
            )}
          </div>
        </div>
        {member && (
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-mono font-bold text-zinc-950 shrink-0"
            style={{ backgroundColor: colorFor(member.id || member.user_id) }}
            title={displayName(member)}
          >
            {initials(displayName(member))}
          </div>
        )}
        {!member && (
          <div
            className="w-6 h-6 rounded-full border border-dashed border-zinc-700 flex items-center justify-center shrink-0"
            title="Unassigned"
          >
            <UserIcon className="w-2.5 h-2.5 text-zinc-600" />
          </div>
        )}
      </div>
    </li>
  );
}
